import StreamingAvatar, {
    ConnectionQuality,
    StartAvatarRequest,
    StreamingEvents,
    StreamingTalkingMessageEvent,
    UserTalkingMessageEvent,
} from "@heygen/streaming-avatar";
import { action, makeAutoObservable, observable } from "mobx";
import {
    MessageSender,
    StreamingAvatarSessionState,
} from "@/features/avatar/const";
import { Message } from "@/shared/types/avatar";

export class StreamingAvatarStore {
    avatarStream: StreamingAvatar | null = null;
    basePath?: string;

    // Voice Chat State
    isMuted = true;
    isVoiceChatLoading = false;
    isVoiceChatActive = false;

    // Session State
    @observable
    sessionState: StreamingAvatarSessionState =
        StreamingAvatarSessionState.INACTIVE;
    stream: MediaStream | null = null;

    // Messages
    messages: Message[] = [];
    private currentSender: MessageSender | null = null;

    // Listening & Talking
    isListening = false;
    isUserTalking = false;
    isAvatarTalking = false;

    // Connection
    connectionQuality: ConnectionQuality = ConnectionQuality.UNKNOWN;

    constructor(basePath?: string) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.basePath = basePath;
    }

    /** Init with token */
    init(token: string) {
        this.avatarStream = new StreamingAvatar({
            token,
            basePath: this.basePath,
        });

        this.avatarStream.on(
            StreamingEvents.AVATAR_START_TALKING,
            (e: StreamingTalkingMessageEvent) => {
                console.log("Avatar started talking", e);
            },
        );
        this.avatarStream.on(
            StreamingEvents.AVATAR_STOP_TALKING,
            (e: StreamingTalkingMessageEvent) => {
                console.log("Avatar stopped talking", e);
            },
        );
        this.avatarStream.on(StreamingEvents.STREAM_DISCONNECTED, () => {
            console.log("Stream disconnected");
        });
        this.avatarStream.on(
            StreamingEvents.STREAM_READY,
            (event: { detail: string }) => {
                console.log(">>>>> Stream ready:", event.detail);
            },
        );
        this.avatarStream.on(
            StreamingEvents.USER_START,
            (event: StreamingTalkingMessageEvent) => {
                console.log(">>>>> User started talking:", event);
            },
        );
        this.avatarStream.on(
            StreamingEvents.USER_STOP,
            (event: { detail: string }) => {
                console.log(">>>>> User stopped talking:", event);
            },
        );
        this.avatarStream.on(
            StreamingEvents.USER_END_MESSAGE,
            (event: UserTalkingMessageEvent) => {
                console.log(">>>>> User end message:", event);
            },
        );
        this.avatarStream.on(
            StreamingEvents.USER_TALKING_MESSAGE,
            (event: UserTalkingMessageEvent) => {
                console.log(">>>>> User talking message:", event);
            },
        );
        this.avatarStream.on(
            StreamingEvents.AVATAR_TALKING_MESSAGE,
            (event: UserTalkingMessageEvent) => {
                console.log(">>>>> Avatar talking message:", event);
            },
        );
        this.avatarStream.on(
            StreamingEvents.AVATAR_END_MESSAGE,
            (event: UserTalkingMessageEvent) => {
                console.log(">>>>> Avatar end message:", event);
            },
        );
        return this.avatarStream;
    }

    /** Handle incoming MediaStream */
    @action
    private handleStream = ({ detail }: { detail: MediaStream }) => {
        this.stream = detail;
        this.sessionState = StreamingAvatarSessionState.CONNECTED;
    };

    /** Stop session */
    async stop() {
        if (!this.avatarStream) return;
        this.avatarStream.off(StreamingEvents.STREAM_READY, this.handleStream);
        this.avatarStream.off(StreamingEvents.STREAM_DISCONNECTED, this.stop);

        this.isListening = false;
        this.isUserTalking = false;
        this.isAvatarTalking = false;
        this.stream = null;

        await this.avatarStream.stopAvatar();
        this.avatarStream = null;
        this.sessionState = StreamingAvatarSessionState.INACTIVE;
    }

    /** Start session */
    async start(config: StartAvatarRequest, token?: string) {
        if (this.sessionState !== StreamingAvatarSessionState.INACTIVE) {
            throw new Error("There is already an active session");
        }

        if (!this.avatarStream) {
            if (!token) throw new Error("Token is required");
            this.init(token);
        }
        if (!this.avatarStream) throw new Error("Avatar is not initialized");

        this.sessionState = StreamingAvatarSessionState.CONNECTING;

        // register event listeners
        this.avatarStream.on(StreamingEvents.STREAM_READY, this.handleStream);
        this.avatarStream.on(StreamingEvents.STREAM_DISCONNECTED, this.stop);
        this.avatarStream.on(
            StreamingEvents.CONNECTION_QUALITY_CHANGED,
            ({ detail }) => {
                this.connectionQuality = detail;
            },
        );
        this.avatarStream.on(StreamingEvents.USER_START, () => {
            this.isUserTalking = true;
        });
        this.avatarStream.on(StreamingEvents.USER_STOP, () => {
            this.isUserTalking = false;
        });
        this.avatarStream.on(StreamingEvents.AVATAR_START_TALKING, () => {
            this.isAvatarTalking = true;
        });
        this.avatarStream.on(StreamingEvents.AVATAR_STOP_TALKING, () => {
            this.isAvatarTalking = false;
        });

        await this.avatarStream.createStartAvatar(config);
        return this.avatarStream;
    }

    // Actions
    setIsMuted(val: boolean) {
        this.isMuted = val;
    }
    setIsVoiceChatLoading(val: boolean) {
        this.isVoiceChatLoading = val;
    }
    setIsVoiceChatActive(val: boolean) {
        this.isVoiceChatActive = val;
    }

    setSessionState(val: StreamingAvatarSessionState) {
        this.sessionState = val;
    }
    setStream(val: MediaStream | null) {
        this.stream = val;
    }

    clearMessages() {
        this.messages = [];
        this.currentSender = null;
    }

    handleUserTalkingMessage({ detail }: { detail: UserTalkingMessageEvent }) {
        if (this.currentSender === MessageSender.CLIENT) {
            const last = this.messages[this.messages.length - 1];
            this.messages[this.messages.length - 1] = {
                ...last,
                content: last.content + detail.message,
            };
        } else {
            this.currentSender = MessageSender.CLIENT;
            this.messages.push({
                id: Date.now().toString(),
                sender: MessageSender.CLIENT,
                content: detail.message,
            });
        }
    }

    handleStreamingTalkingMessage({
        detail,
    }: {
        detail: StreamingTalkingMessageEvent;
    }) {
        if (this.currentSender === MessageSender.AVATAR) {
            const last = this.messages[this.messages.length - 1];
            this.messages[this.messages.length - 1] = {
                ...last,
                content: last.content + detail.message,
            };
        } else {
            this.currentSender = MessageSender.AVATAR;
            this.messages.push({
                id: Date.now().toString(),
                sender: MessageSender.AVATAR,
                content: detail.message,
            });
        }
    }

    handleEndMessage() {
        this.currentSender = null;
    }

    setIsListening(val: boolean) {
        this.isListening = val;
    }
    setIsUserTalking(val: boolean) {
        this.isUserTalking = val;
    }
    setIsAvatarTalking(val: boolean) {
        this.isAvatarTalking = val;
    }

    setConnectionQuality(val: ConnectionQuality) {
        this.connectionQuality = val;
    }
}
